/**
 * Raster stroke lifecycle with auto-wrap — count-buffer revision.
 *
 * The sketch layer now accumulates *touch counts* (Float32Array) rather than
 * painting pixel colours. The render canvas is a derived view of those counts,
 * rebuilt on theme change and — during a stroke — lazily via a dirty-rect +
 * flushDirty pattern: each touch sample only marks its own pixel bounds dirty
 * (cheap), and the actual canvas rebuild happens at most once per animation
 * frame (TileCanvas's render loop calls flushDirty before compositing) rather
 * than once per touch sample. Switching between light (graphite) and dark
 * (neon-green glow) modes repaints all existing marks for free.
 *
 * Ink strokes still use the temp 512² stroke canvas / composite-once path
 * (uniform opacity, no build-up at joints). Only the sketch path changes.
 *
 * Eraser semantics (count-buffer revision):
 *   - Zeroes counts outright in the erased disc (no decrement).
 *   - On S size: hard disc boundary.
 *   - On M/L sizes: linear alpha ramp over the outer 25% of the radius so the
 *     erase blends softly at the edge (counts lerp toward zero rather than snap).
 *
 * Seamlessness: every painted segment / stamp near a tile edge is duplicated
 * through each ring-1 symmetry transform, so the raster wraps exactly.
 */
import {
	apply,
	compose,
	invert,
	transformToMatrix,
	type Mat,
} from '@/geometry/affine'
import { floodFillTile } from '@/geometry/floodFill'
import { pointInPolygon } from '@/geometry/wrap'
import { classifyColour, paletteIndicesFor } from '@/patterns/wallpaper'
import {
	isDarkMode,
	renderCountsToCanvas,
	TILE_RASTER,
	useLayersStore,
	type InkGroup,
	type SketchGroup,
} from '@/stores/layers'
import { usePatternStore } from '@/stores/pattern'
import { SKETCH_GRAPHITE, useToolsStore } from '@/stores/tools'
import { ref } from 'vue'
import {
	inkWidthMul,
	NO_PEN_INPUT,
	N_TILT_BUCKETS,
	pencilWeightMul,
	tiltBucket,
	type PenInput,
} from './penDynamics'
import { useWrapTransform } from './useWrapTransform'

/** max world-segment length before subdivision (tile units) */
const SUBDIVIDE_STEP = 0.015
/** segments fully inside this inset box skip the symmetry duplication */
const SAFE_INSET = 0.12
/**
 * Per-channel colour tolerance for the bucket fill.
 */
const FILL_TOLERANCE = 96

/**
 * Parse a `#rgb` / `#rrggbb` colour to [r, g, b] (0–255). The ink colour comes
 * from an `<input type="color">` (always `#rrggbb`) so this stays deliberately
 * small; falls back to mid-grey for anything unparseable.
 */
export function parseHexColor(hex: string): [number, number, number] {
	let h = hex.trim().replace(/^#/, '')
	if (h.length === 3) h = h[0]! + h[0]! + h[1]! + h[1]! + h[2]! + h[2]!
	if (h.length !== 6 || /[^0-9a-fA-F]/.test(h)) return [128, 128, 128]
	return [
		parseInt(h.slice(0, 2), 16),
		parseInt(h.slice(2, 4), 16),
		parseInt(h.slice(4, 6), 16),
	]
}

// Polygon raster masks are pure functions of the pattern — cache per id.
const insideMaskCache = new Map<string, Uint8Array>()

function getInsideMask(
	patternId: string,
	polygon: [number, number][],
): Uint8Array {
	let mask = insideMaskCache.get(patternId)
	if (mask) return mask
	mask = new Uint8Array(TILE_RASTER * TILE_RASTER)
	for (let y = 0; y < TILE_RASTER; y++) {
		for (let x = 0; x < TILE_RASTER; x++) {
			if (
				pointInPolygon(
					polygon,
					(x + 0.5) / TILE_RASTER,
					(y + 0.5) / TILE_RASTER,
					1e-7,
				)
			) {
				mask[y * TILE_RASTER + x] = 1
			}
		}
	}
	insideMaskCache.set(patternId, mask)
	return mask
}

// ---- sketch speckle (graphite-grain / glow-grain) stamp sprites ----
//
// Each sprite is a disc-shaped boolean mask — which pixels within the stamp box
// get a touch. Colour is NOT baked in; the render function reads the count
// buffer and applies theme colour at display time.
//
// The stamp weight written per touched pixel is STAMP_WEIGHT × weightMul (from
// pressure) × the bucket's weightTaper (from tilt). A single stroke pass
// produces counts in [0, ~4×] depending on spacing overlap; the render curve
// maps those to pleasant alphas.
//
// Tilt spreads the mark by widening the stamp radius. Rather than regenerate a
// sprite per stamp (which would allocate in the touch hot path), we cache a
// small fixed set of sprite sets keyed by theme + tilt bucket — bucket 0 is the
// upright radius (identical geometry to the old fixed disc).
// Halved from 2px/0.9px (radius/spacing scaled together, keeping the same
// ratio, so the pencil reads as a smaller but equally dense mark rather than
// a sparser/grainier one at the old spacing).
const STAMP_RADIUS_PX = 1
const STAMP_SPACING_PX = 0.45
/**
 * §3: stamps are clipped to the fundamental domain, expanded by this epsilon
 * (tile units, 1 tile = TILE_RASTER px), so an over-boundary overshoot isn't
 * stamped/duplicated into a neighbouring domain (the live colour-bleed) while
 * marks that legitimately reach the shared edge still stamp. Kept a hair wider
 * than TileCanvas's seam-closing clip outset (CLIP_OUTSET_PX ≈ 0.75 device px,
 * up to ~2–3 buffer px when zoomed out) so that outset still finds buffer
 * content just past the edge and doesn't reopen the Bug-5 seams. Tunable —
 * needs on-device confirmation per the brief (too big → bleed returns, too
 * small → hairline seams).
 */
const STAMP_CLIP_EPS = 3 / 512
const FILL_FRACTION = 0.5
/**
 * Weight added to the count buffer per touched pixel per stamp, at full weight.
 * This is the per-touch increment; pressure/tilt scale it down or (firm press)
 * modestly up. §5: lowered 0.1 → 0.05 so a single pass reads as a light pencil
 * mark and density builds up over several overlapping passes toward the
 * exponential render curve's saturation, rather than one pass laying down a
 * near-committed line. Feel-tuning value — flagged for on-device confirmation
 * ("how many passes to full" is a judgement call best made against real touch).
 */
const STAMP_WEIGHT = 0.05
/**
 * How much wider the stamp radius gets at maximum tilt (× STAMP_RADIUS_PX).
 * Was 0.75 (2px → 3.5px radius, ~4px → ~7px diameter) — confirmed on-device
 * to be imperceptible: too small a change to read against a dense, speckled
 * stroke. 3.0 takes the diameter from ~4px to ~16px at max tilt, which
 * combined with the taper below deposits roughly 8x the "ink" per stamp at
 * full tilt vs upright — a clearly visible difference, not just a wiring
 * change that happens to be mathematically present.
 */
const TILT_MAX_RADIUS_GAIN = 3.0
const N_SPECKLE_SPRITES = 8

/** A sprite is a flat Uint8Array mask (1 = touched, 0 = untouched) over d². */
type SpeckleSprite = Uint8Array

/** One cached tilt bucket: its sprite set + box size + weight taper. */
interface StampBucket {
	sprites: SpeckleSprite[]
	/** side length of the (square) stamp box in pixels */
	d: number
	/**
	 * Per-pixel weight scale for this bucket. Wider (tilted) stamps spread the
	 * same "graphite" over more pixels, so we taper the per-pixel weight down as
	 * the radius grows (√ of the radius ratio — a mild taper) to keep a laid-flat
	 * stroke from reading disproportionately darker than an upright one.
	 */
	weightTaper: number
}

/** Radius in pixels for a tilt bucket (bucket 0 = upright = STAMP_RADIUS_PX),
 *  scaled by the artist's S/M/L pencil size (`tools.sketchSizeMul`, brief
 *  09-layout's "Brush size" control — M leaves this identical to the old
 *  single fixed radius). */
function bucketRadiusPx(bucket: number, sizeMul: number): number {
	const factor = N_TILT_BUCKETS > 1 ? bucket / (N_TILT_BUCKETS - 1) : 0
	return STAMP_RADIUS_PX * sizeMul * (1 + TILT_MAX_RADIUS_GAIN * factor)
}

/** Pre-render the speckle sprite set for one (theme, tilt bucket, pencil size). */
const stampBucketCache = new Map<string, StampBucket>()

function getStampBucket(bucket: number, sizeMul: number): StampBucket {
	const key = `${isDarkMode() ? 'dark' : 'light'}:${bucket}:${sizeMul}`
	let entry = stampBucketCache.get(key)
	if (entry) return entry
	const radiusPx = bucketRadiusPx(bucket, sizeMul)
	const d = Math.ceil(radiusPx * 2) + 2 // pad so the disc never clips the box
	const c = (d - 1) / 2
	const sprites: SpeckleSprite[] = []
	for (let s = 0; s < N_SPECKLE_SPRITES; s++) {
		const mask = new Uint8Array(d * d)
		for (let y = 0; y < d; y++) {
			for (let x = 0; x < d; x++) {
				const dist = Math.hypot(x - c, y - c)
				if (dist > radiusPx) continue
				if (Math.random() > FILL_FRACTION * (1 - dist / radiusPx)) continue
				mask[y * d + x] = 1
			}
		}
		sprites.push(mask)
	}
	entry = { sprites, d, weightTaper: Math.sqrt(STAMP_RADIUS_PX / radiusPx) }
	stampBucketCache.set(key, entry)
	return entry
}

export function useDrawing() {
	const patternStore = usePatternStore()
	const layers = useLayersStore()
	const tools = useToolsStore()
	const { findPlacement } = useWrapTransform()

	/** true while a pen gesture is active (live preview) */
	const drawingActive = ref(false)
	/** bumped per input batch — drives canvas redraw during gestures */
	const gestureTick = ref(0)

	let strokeCanvas: HTMLCanvasElement | null = null
	let frame: Mat | null = null
	let lastWorld: [number, number] | null = null
	let lastTile: [number, number] | null = null
	let erasedAnything = false
	let penDirect = false
	/** sketch count-buffer snapshot (all LEGACY origin-class buffers, pre-colour)
	 *  taken before a sketch stroke/erase, for its paint-sketch undo. */
	let sketchBefore: Map<string, Float32Array> | null = null
	/** true while the active op targets an INK layer (direct per-origin-class
	 *  rasters) rather than a SKETCH layer (count buffer). */
	let inkActive = false
	/** ink paint-group snapshot taken before an ink stroke/erase, for undo. */
	let inkBefore: InkGroup[] | null = null
	/** true while the current sketch stroke is going into a frozen colour
	 *  paint group (tools.sketchColourActive at stroke start) rather than the
	 *  legacy per-class buffers — brief 10-colours: once real colour is
	 *  active, marks are immune to later colour/symmetry changes, exactly
	 *  like ink. Captured once at stroke start, like strokePalette/strokeCount. */
	let colourGroupActive = false
	/** sketch colour-group snapshot taken before a coloured sketch stroke/
	 *  erase, for its paint-sketch-groups undo. */
	let sketchGroupsBefore: SketchGroup[] | null = null
	/** swatch-index palette + count frozen at the start of the current ink or
	 *  coloured-sketch stroke, so the stroke coalesces into (or creates) the
	 *  matching paint group and is immune to later colour/theme changes. */
	let strokePalette: number[] = []
	let strokeCount = 1
	/**
	 * Colour-class of the cell the stroke is currently in — the ORIGIN class its
	 * marks are attributed to (colour travels with the stroke). Set at stroke
	 * start and recomputed on every wrap. `classOfFrame` maps a composed frame
	 * matrix back to its class via the enumerated transform set.
	 */
	let originClass = 0
	let classOfFrame: (m: Mat) => number = () => 0
	/** tile-space distance walked since the last speckle stamp (resampling carry) */
	let stampCarry = 0
	/** ring-1 inverse transforms for stamp/erase duplication, cached per stroke */
	let ring1Inv: Mat[] | null = null
	/**
	 * Per-count-buffer dirty rectangles touched since the last flush. Rebuilding
	 * a render canvas is expensive (see renderCountsToCanvas) so during a stroke
	 * we only track what's dirty and flush once per animation frame (flushDirty,
	 * from TileCanvas's render loop). Keyed by count-buffer id, because a
	 * coloured sketch stroke spreads across several per-origin-class buffers as
	 * it crosses cells. `currentCountId` is the buffer the next stamp writes to.
	 */
	type Rect = { x0: number; y0: number; x1: number; y1: number }
	const dirty = new Map<string, Rect>()
	let currentCountId = ''

	function markDirty(x0: number, y0: number, x1: number, y1: number): void {
		if (x1 <= 0 || y1 <= 0 || x0 >= TILE_RASTER || y0 >= TILE_RASTER) return
		const d = dirty.get(currentCountId)
		if (!d) {
			dirty.set(currentCountId, { x0, y0, x1, y1 })
		} else {
			d.x0 = Math.min(d.x0, x0)
			d.y0 = Math.min(d.y0, y0)
			d.x1 = Math.max(d.x1, x1)
			d.y1 = Math.max(d.y1, y1)
		}
	}

	/** Render whatever's dirty since the last flush. Cheap no-op if nothing pending. */
	function flushDirty(): void {
		if (!dirty.size) return
		for (const [id, rect] of dirty) renderCountsToCanvas(id, rect)
		dirty.clear()
	}

	function getStrokeCanvas(): HTMLCanvasElement {
		if (!strokeCanvas) {
			strokeCanvas = document.createElement('canvas')
			strokeCanvas.width = TILE_RASTER
			strokeCanvas.height = TILE_RASTER
		}
		return strokeCanvas
	}

	/** Sketch paints graphite (pencil); ink uses the user's chosen colour. */
	function brushColor(): string {
		return layers.activeLayer === 'sketch' ? SKETCH_GRAPHITE : tools.color
	}

	/** Ink line width from the S/M/L selector (sketch uses the fixed stamp). */
	function penSize(): number {
		return tools.inkSizeTile
	}

	// --- Origin-class tracking (colour travels with the stroke) ---

	/** Round a matrix to a stable key for lookup against the enumerated set. */
	function matKey(m: Mat): string {
		return m.map((v) => Math.round(v * 4096) / 4096).join(',')
	}

	/**
	 * Build a matrix→colour-class lookup for the current variant + colour count,
	 * so a composed frame (which is always some group element) can be mapped back
	 * to the class of the cell it represents. Range 3 covers a wide neighbourhood;
	 * a frame beyond it falls back to class 0.
	 */
	function buildClassLookup(): (m: Mat) => number {
		const n = tools.colourCount
		if (n <= 1) return () => 0
		const variant = patternStore.currentVariant
		const wpgId = patternStore.currentConfig.wpgId
		const map = new Map<string, number>()
		for (const t of variant.transformsInRange(3)) {
			map.set(matKey(transformToMatrix(t)), classifyColour(wpgId, n, t) ?? 0)
		}
		return (m: Mat) => map.get(matKey(m)) ?? 0
	}

	/** The 2D context to draw into for the current stroke's frozen palette +
	 *  current origin class (colour travels with the stroke, frozen at draw time). */
	function inkCtx(): CanvasRenderingContext2D {
		return layers
			.getInkDrawTarget(layers.activeRasterLayer.id, strokePalette, strokeCount, originClass)
			.getContext('2d')!
	}

	/** Erase a segment across ALL of the active ink layer's rasters, every group
	 *  (eraser removes whatever's visually in the layer — user's choice). */
	function eraseInkSegment(p0: [number, number], p1: [number, number]): void {
		for (const canvas of layers.eachInkCanvas(layers.activeRasterLayer.id)) {
			paintSegment(canvas.getContext('2d')!, p0, p1, tools.eraseSizeTile, true)
		}
	}

	/**
	 * Paint one tile-space segment into `ctx`, duplicated through ring-1 symmetry
	 * transforms when near an edge. Used for ink strokes and eraser on the
	 * canvas directly; sketch strokes go through stampSegment / count buffer instead.
	 */
	function paintSegment(
		ctx: CanvasRenderingContext2D,
		p0: [number, number],
		p1: [number, number],
		size: number,
		erase: boolean,
	): void {
		const r = size / 2
		const safe =
			Math.min(p0[0], p0[1], p1[0], p1[1]) - r > SAFE_INSET &&
			Math.max(p0[0], p0[1], p1[0], p1[1]) + r < 1 - SAFE_INSET

		ctx.lineCap = 'round'
		ctx.lineJoin = 'round'
		ctx.lineWidth = size
		ctx.globalCompositeOperation = erase ? 'destination-out' : 'source-over'
		ctx.strokeStyle = erase ? '#000' : brushColor()

		const transforms = safe
			? [null]
			: patternStore.currentVariant
					.transformsInRange(1)
					.map((t) => invert(transformToMatrix(t)))

		for (const inv of transforms) {
			if (inv === null) {
				ctx.setTransform(TILE_RASTER, 0, 0, TILE_RASTER, 0, 0)
			} else {
				ctx.setTransform(
					TILE_RASTER * inv[0],
					TILE_RASTER * inv[1],
					TILE_RASTER * inv[2],
					TILE_RASTER * inv[3],
					TILE_RASTER * inv[4],
					TILE_RASTER * inv[5],
				)
			}
			ctx.beginPath()
			ctx.moveTo(p0[0], p0[1])
			ctx.lineTo(p1[0], p1[1])
			ctx.stroke()
		}
		ctx.setTransform(1, 0, 0, 1, 0, 0)
		ctx.globalCompositeOperation = 'source-over'
	}

	// ---------------------------------------------------------------------------
	// Count-buffer stamp helpers
	// ---------------------------------------------------------------------------

	/**
	 * Write one speckle stamp into the count buffer at tile-space (cx, cy).
	 * `bucketEntry` (tilt) selects the sprite geometry; `weight` is the already
	 * resolved per-pixel weight (STAMP_WEIGHT × pressure × taper), computed once
	 * per input sample by the caller so the hot path stays allocation-free.
	 * Near edges, also stamps through ring-1 symmetry copies for seamless wrap.
	 */
	function stampCountsAt(
		counts: Float32Array,
		cx: number,
		cy: number,
		bucketEntry: StampBucket,
		weight: number,
	): void {
		const { sprites, d } = bucketEntry
		const sprite = sprites[(Math.random() * sprites.length) | 0]!
		const half = d / 2
		const r = (d / 2) / TILE_RASTER
		const safe =
			cx - r > SAFE_INSET &&
			cy - r > SAFE_INSET &&
			cx + r < 1 - SAFE_INSET &&
			cy + r < 1 - SAFE_INSET

		const writeStamp = (ox: number, oy: number) => {
			// Top-left pixel of the stamp in tile pixels (may be off-canvas — clamp).
			const x0 = Math.round(ox * TILE_RASTER - half)
			const y0 = Math.round(oy * TILE_RASTER - half)
			for (let sy = 0; sy < d; sy++) {
				const py = y0 + sy
				if (py < 0 || py >= TILE_RASTER) continue
				for (let sx = 0; sx < d; sx++) {
					if (!sprite[sy * d + sx]) continue
					const px = x0 + sx
					if (px < 0 || px >= TILE_RASTER) continue
					counts[py * TILE_RASTER + px]! += weight
				}
			}
			markDirty(x0, y0, x0 + d, y0 + d)
		}

		writeStamp(cx, cy)
		if (!safe) {
			if (!ring1Inv) {
				ring1Inv = patternStore.currentVariant
					.transformsInRange(1)
					.map((t) => invert(transformToMatrix(t)))
			}
			for (const inv of ring1Inv) {
				const [tx, ty] = apply(inv, [cx, cy])
				writeStamp(tx, ty)
			}
		}
	}

	/**
	 * Walk p0→p1 in tile space, stamping counts at a fixed spacing
	 * (carry-continuous). Each stamp is clipped to the fundamental-domain
	 * `polygon` (§3): a point outside the domain is skipped, so an over-boundary
	 * overshoot never gets stamped or ring-1-duplicated into a neighbouring
	 * domain (that was the live colour-bleed). The continuation resumes,
	 * in-domain, in the wrapped frame (see step's wrap). `STAMP_CLIP_EPS`
	 * expands the domain slightly so marks that should reach the shared edge
	 * aren't cut short (and so the render's seam-closing clip outset still finds
	 * buffer content just past the edge).
	 */
	function stampCountsSegment(
		counts: Float32Array,
		p0: [number, number],
		p1: [number, number],
		polygon: [number, number][],
		bucketEntry: StampBucket,
		weight: number,
	): void {
		const spacing = STAMP_SPACING_PX / TILE_RASTER
		const dx = p1[0] - p0[0]
		const dy = p1[1] - p0[1]
		const segLen = Math.hypot(dx, dy)
		if (segLen < 1e-9) return
		const ux = dx / segLen
		const uy = dy / segLen
		let pos = spacing - stampCarry
		while (pos <= segLen) {
			const sx = p0[0] + ux * pos
			const sy = p0[1] + uy * pos
			if (pointInPolygon(polygon, sx, sy, STAMP_CLIP_EPS)) {
				stampCountsAt(counts, sx, sy, bucketEntry, weight)
			}
			pos += spacing
		}
		stampCarry = segLen - (pos - spacing)
	}

	// ---------------------------------------------------------------------------
	// Count-buffer erase helpers
	// ---------------------------------------------------------------------------

	/**
	 * Zero (or soft-zero) counts in a disc centred at tile-space (cx, cy).
	 *
	 * Soft edge: for M and L eraser sizes (eraseSizeTile ≥ a threshold), pixels
	 * in the outer 25% of the radius are linearly faded toward zero rather than
	 * snapped. S size is always hard.
	 *
	 * Also duplicated through ring-1 transforms near edges.
	 */
	function eraseCountsAt(counts: Float32Array, cx: number, cy: number): void {
		const sizeT = tools.eraseSizeTile
		const radiusPx = (sizeT / 2) * TILE_RASTER
		// Soft edge for M/L: anything wider than ~4 tile-pixels radius gets a ramp.
		const softEdge = radiusPx > 4
		const softStart = softEdge ? 0.75 : 1.0 // inner fraction that is hard-zeroed

		const writeErase = (ox: number, oy: number) => {
			const cxPx = ox * TILE_RASTER
			const cyPx = oy * TILE_RASTER
			const x0 = Math.max(0, Math.floor(cxPx - radiusPx))
			const x1 = Math.min(TILE_RASTER - 1, Math.ceil(cxPx + radiusPx))
			const y0 = Math.max(0, Math.floor(cyPx - radiusPx))
			const y1 = Math.min(TILE_RASTER - 1, Math.ceil(cyPx + radiusPx))
			for (let py = y0; py <= y1; py++) {
				for (let px = x0; px <= x1; px++) {
					const dist = Math.hypot(px - cxPx, py - cyPx)
					if (dist > radiusPx) continue
					const idx = py * TILE_RASTER + px
					if (!softEdge || dist <= radiusPx * softStart) {
						counts[idx] = 0
					} else {
						// linear ramp: at softStart×r → count unchanged, at r → count = 0
						const t =
							(dist - radiusPx * softStart) / (radiusPx * (1 - softStart))
						counts[idx]! *= 1 - t
					}
				}
			}
			markDirty(x0, y0, x1 + 1, y1 + 1)
		}

		const r = sizeT / 2
		const safe =
			cx - r > SAFE_INSET &&
			cy - r > SAFE_INSET &&
			cx + r < 1 - SAFE_INSET &&
			cy + r < 1 - SAFE_INSET
		writeErase(cx, cy)
		if (!safe) {
			if (!ring1Inv) {
				ring1Inv = patternStore.currentVariant
					.transformsInRange(1)
					.map((t) => invert(transformToMatrix(t)))
			}
			for (const inv of ring1Inv) {
				const [tx, ty] = apply(inv, [cx, cy])
				writeErase(tx, ty)
			}
		}
	}

	// ---------------------------------------------------------------------------
	// Core advance loop
	// ---------------------------------------------------------------------------

	/** Advance one (already short) world step, painting/stamping and wrapping. */
	function step(
		wx: number,
		wy: number,
		erase: boolean,
		bucketEntry: StampBucket,
		stampWeight: number,
		inkSize: number,
	): void {
		if (!frame || !lastTile) return
		const polygon = patternStore.currentPattern.polygon()
		const p = apply(invert(frame), [wx, wy])
		const layerId = layers.activeRasterLayer.id
		if (inkActive && erase) {
			// Ink eraser: destination-out across every class raster of the layer.
			eraseInkSegment(lastTile, p)
		} else if (inkActive) {
			// Ink: paint into the current origin-class raster (colour travels with
			// the stroke). Stored as alpha geometry; tinted per placement at render.
			paintSegment(inkCtx(), lastTile, p, inkSize, false)
		} else if (erase) {
			// Sketch eraser: zero counts in a disc across every origin-class buffer,
			// legacy AND every frozen colour group (removes whatever's visually
			// there, any colour epoch — brief 10-colours, same choice as ink).
			for (const cls of layers.sketchClassList(layerId)) {
				currentCountId = layers.sketchCountId(layerId, cls)
				eraseCountsAt(layers.getSketchCounts(layerId, cls), p[0], p[1])
			}
			for (const { counts, id } of layers.eachSketchGroupCounts(layerId)) {
				currentCountId = id
				eraseCountsAt(counts, p[0], p[1])
			}
		} else if (colourGroupActive) {
			// Coloured sketch: colour travels with the stroke AND is frozen in
			// this stroke's paint group (brief 10-colours) — a later colour or
			// symmetry change won't recolour it, exactly like ink.
			const { counts, id } = layers.getSketchGroupCounts(layerId, strokePalette, strokeCount, originClass)
			currentCountId = id
			stampCountsSegment(counts, lastTile, p, polygon, bucketEntry, stampWeight)
		} else {
			// Sketch: write into the current ORIGIN-class count buffer — colour
			// travels with the stroke, exactly like ink (clipped to domain — §3).
			currentCountId = layers.sketchCountId(layerId, originClass)
			stampCountsSegment(
				layers.getSketchCounts(layerId, originClass),
				lastTile,
				p,
				polygon,
				bucketEntry,
				stampWeight,
			)
		}
		if (pointInPolygon(polygon, p[0], p[1], 1e-9)) {
			lastTile = p
			return
		}
		const wr = patternStore.currentVariant.wrapPoint(p[0], p[1])
		frame = compose(frame, transformToMatrix(wr.transform))
		lastTile = [wr.x, wr.y]
		// Crossed into a new cell — the marks that follow belong to that cell's
		// colour class (their symmetry copies shift relative to it). Applies to
		// both ink and coloured sketch.
		if (strokeCount > 1) originClass = classOfFrame(frame)
	}

	function advance(wx: number, wy: number, erase: boolean, input: PenInput): void {
		if (!lastWorld) return
		// Resolve pressure/tilt → stamp geometry + weight once per input sample so
		// the per-stamp inner loop stays allocation-free and O(1).
		const bucketEntry = getStampBucket(tiltBucket(input.tiltMag), tools.sketchSizeMul)
		const stampWeight =
			STAMP_WEIGHT * pencilWeightMul(input) * bucketEntry.weightTaper
		const inkSize = penSize() * inkWidthMul(input)
		const [px, py] = lastWorld
		const dist = Math.hypot(wx - px, wy - py)
		const n = Math.max(1, Math.ceil(dist / SUBDIVIDE_STEP))
		for (let i = 1; i <= n; i++) {
			step(px + ((wx - px) * i) / n, py + ((wy - py) * i) / n, erase, bucketEntry, stampWeight, inkSize)
		}
		lastWorld = [wx, wy]
		// Sketch counts are already updated (step marks the touched region dirty);
		// the canvas rebuild is deferred to flushDirty() once per frame. Ink is
		// painted straight onto its class rasters, shown live by the render loop.
		gestureTick.value++
	}

	// ---------------------------------------------------------------------------
	// Pen
	// ---------------------------------------------------------------------------

	function startStroke(wx: number, wy: number, input: PenInput = NO_PEN_INPUT): boolean {
		const placement = findPlacement(wx, wy)
		if (!placement) return false
		frame = placement.matrix
		lastWorld = [wx, wy]
		lastTile = placement.tilePoint
		inkActive = layers.activeLayer === 'ink'
		penDirect = !inkActive
		drawingActive.value = true
		ring1Inv = null
		if (inkActive) {
			// Ink: paint directly into the origin-class raster of the cell we started
			// in; colour travels with the stroke and is FROZEN in this stroke's paint
			// group (a later colour change won't recolour it). Undo snapshots groups.
			const layerId = layers.activeRasterLayer.id
			strokePalette = paletteIndicesFor(tools.baseColourIndex, tools.colourCount)
			strokeCount = tools.colourCount
			classOfFrame = buildClassLookup()
			// Classify the START cell straight from its transform (accurate at any
			// distance from the origin tile); wraps re-classify via classOfFrame.
			originClass = strokeCount > 1 ? classifyColour(patternStore.currentConfig.wpgId, strokeCount, placement.transform) ?? 0 : 0
			inkBefore = layers.snapshotInkGroups(layerId)
			paintSegment(inkCtx(), placement.tilePoint, placement.tilePoint, penSize() * inkWidthMul(input), false)
		} else {
			// Sketch: colour travels with the stroke too — route stamps to the
			// origin-class count buffer (class 0 = base buffer in single-colour
			// mode); once real colour is active, that's a frozen colour paint
			// group instead (brief 10-colours), exactly like ink, so this stroke
			// is immune to a later colour/symmetry change. Undo snapshots
			// whichever set applies.
			const layerId = layers.activeRasterLayer.id
			colourGroupActive = tools.sketchColourActive
			strokeCount = tools.colourCount
			classOfFrame = buildClassLookup()
			originClass = strokeCount > 1 ? classifyColour(patternStore.currentConfig.wpgId, strokeCount, placement.transform) ?? 0 : 0
			stampCarry = 0
			// Initial dot for taps.
			const bucketEntry = getStampBucket(tiltBucket(input.tiltMag), tools.sketchSizeMul)
			const weight = STAMP_WEIGHT * pencilWeightMul(input) * bucketEntry.weightTaper
			if (colourGroupActive) {
				strokePalette = paletteIndicesFor(tools.baseColourIndex, tools.colourCount)
				sketchGroupsBefore = layers.snapshotSketchGroups(layerId)
				const { counts, id } = layers.getSketchGroupCounts(layerId, strokePalette, strokeCount, originClass)
				currentCountId = id
				stampCountsAt(counts, placement.tilePoint[0], placement.tilePoint[1], bucketEntry, weight)
			} else {
				sketchBefore = layers.snapshotSketch(layerId)
				currentCountId = layers.sketchCountId(layerId, originClass)
				stampCountsAt(
					layers.getSketchCounts(layerId, originClass),
					placement.tilePoint[0],
					placement.tilePoint[1],
					bucketEntry,
					weight,
				)
			}
			renderCountsToCanvas(currentCountId)
			dirty.clear()
		}
		gestureTick.value++
		return true
	}

	function continueStroke(wx: number, wy: number, input: PenInput = NO_PEN_INPUT): void {
		if (!frame) return
		advance(wx, wy, false, input)
	}

	function endStroke(): void {
		if (!frame) return
		const layerId = layers.activeRasterLayer.id
		if (inkActive) {
			if (inkBefore) layers.commitInk(layerId, inkBefore)
		} else {
			// Guarantee the final pixels are rendered before this stroke is
			// committed/saved/thumbnailed — don't wait for the next animation frame.
			flushDirty()
			if (colourGroupActive) {
				if (sketchGroupsBefore) layers.commitSketchGroups(layerId, sketchGroupsBefore)
			} else if (sketchBefore) {
				layers.commitSketch(layerId, sketchBefore)
			}
		}
		sketchBefore = null
		sketchGroupsBefore = null
		inkBefore = null
		cancelStroke()
	}

	function cancelStroke(): void {
		if (inkActive && inkBefore) {
			// Roll partial ink marks back by restoring the class-raster snapshot.
			layers.restoreInkGroups(layers.activeRasterLayer.id, inkBefore)
		} else if (penDirect && colourGroupActive && sketchGroupsBefore) {
			// Roll back partial coloured-sketch marks by restoring the paint-group snapshot.
			layers.restoreSketchGroups(layers.activeRasterLayer.id, sketchGroupsBefore)
			dirty.clear()
		} else if (penDirect && sketchBefore) {
			// Roll back partial sketch marks by restoring the count-buffer snapshot.
			layers.restoreSketch(layers.activeRasterLayer.id, sketchBefore)
			dirty.clear()
		}
		sketchBefore = null
		sketchGroupsBefore = null
		inkBefore = null
		penDirect = false
		inkActive = false
		drawingActive.value = false
		frame = null
		lastWorld = null
		lastTile = null
		gestureTick.value++
	}

	// ---------------------------------------------------------------------------
	// Eraser (whole drag = one undo step)
	// ---------------------------------------------------------------------------

	function startErase(wx: number, wy: number): boolean {
		const placement = findPlacement(wx, wy)
		if (!placement) return false
		const layerId = layers.activeRasterLayer.id
		inkActive = layers.activeLayer === 'ink'
		erasedAnything = true
		// Mark a gesture active so the renderer live-tints the ink layer being
		// erased (else the ink erase wouldn't show until the drag ends).
		drawingActive.value = true
		frame = placement.matrix
		lastWorld = [wx, wy]
		lastTile = placement.tilePoint
		ring1Inv = null
		if (inkActive) {
			// Ink erase: snapshot the paint groups for undo, erase the initial dot
			// across all rasters (removes whatever's visually there).
			inkBefore = layers.snapshotInkGroups(layerId)
			eraseInkSegment(placement.tilePoint, placement.tilePoint)
		} else {
			// Sketch erase: snapshot all class buffers AND every frozen colour
			// group, erase the initial dot across each (removes whatever's
			// visually there, any colour epoch — brief 10-colours).
			sketchBefore = layers.snapshotSketch(layerId)
			sketchGroupsBefore = layers.hasSketchGroups(layerId) ? layers.snapshotSketchGroups(layerId) : null
			for (const cls of layers.sketchClassList(layerId)) {
				currentCountId = layers.sketchCountId(layerId, cls)
				eraseCountsAt(layers.getSketchCounts(layerId, cls), placement.tilePoint[0], placement.tilePoint[1])
				renderCountsToCanvas(currentCountId)
			}
			for (const { counts, id } of layers.eachSketchGroupCounts(layerId)) {
				currentCountId = id
				eraseCountsAt(counts, placement.tilePoint[0], placement.tilePoint[1])
				renderCountsToCanvas(currentCountId)
			}
			dirty.clear()
		}
		gestureTick.value++
		return true
	}

	function continueErase(wx: number, wy: number): void {
		if (!frame) return
		if (inkActive || sketchBefore) advance(wx, wy, true, NO_PEN_INPUT)
	}

	function endErase(): void {
		const layerId = layers.activeRasterLayer.id
		if (inkActive) {
			if (inkBefore && erasedAnything) layers.commitInk(layerId, inkBefore)
		} else {
			flushDirty()
			if (sketchBefore && erasedAnything) layers.commitSketch(layerId, sketchBefore)
			if (sketchGroupsBefore && erasedAnything) layers.commitSketchGroups(layerId, sketchGroupsBefore)
		}
		sketchBefore = null
		sketchGroupsBefore = null
		inkBefore = null
		inkActive = false
		erasedAnything = false
		drawingActive.value = false
		frame = null
		lastWorld = null
		lastTile = null
	}

	// ---------------------------------------------------------------------------
	// Bucket fill (one click = one undo step; wraps across tile edges)
	// ---------------------------------------------------------------------------

	function fillAt(wx: number, wy: number): boolean {
		const placement = findPlacement(wx, wy)
		if (!placement) return false
		const polygon = patternStore.currentPattern.polygon()
		const inside = getInsideMask(patternStore.currentPatternId, polygon)

		const layerId = layers.activeRasterLayer.id
		const inkFill = layers.activeLayer === 'ink'
		// Flood boundary is read from what's visually in the layer: for ink that's
		// the composite of its class rasters, for sketch its rendered canvas.
		const boundary = inkFill ? layers.compositeInkLayer(layerId) : layers.getLayerCanvas(layerId)
		const ctx = boundary.getContext('2d')!
		const image = ctx.getImageData(0, 0, TILE_RASTER, TILE_RASTER)

		const wrapPixel = (px: number, py: number): [number, number] | null => {
			const wr = patternStore.currentVariant.wrapPoint(
				(px + 0.5) / TILE_RASTER,
				(py + 0.5) / TILE_RASTER,
			)
			const mx = Math.floor(wr.x * TILE_RASTER)
			const my = Math.floor(wr.y * TILE_RASTER)
			if (mx === px && my === py) return null
			return [mx, my]
		}

		const mask = floodFillTile({
			data: image.data,
			size: TILE_RASTER,
			inside,
			wrap: wrapPixel,
			seedX: Math.floor(placement.tilePoint[0] * TILE_RASTER),
			seedY: Math.floor(placement.tilePoint[1] * TILE_RASTER),
			tolerance: FILL_TOLERANCE,
		})
		if (!mask) return false

		if (layers.activeLayer === 'sketch') {
			// Sketch fill: accumulate into the count buffer so it renders in the
			// graphite/glow theme and re-renders correctly on theme change.
			const before = layers.snapshotCounts(layerId)
			const counts = layers.getLayerCounts(layerId)
			// A single "full" pass — the render curve maps this to near-full opacity.
			for (let i = 0; i < mask.length; i++) {
				if (mask[i] === 1) counts[i]! += 0.1
			}
			renderCountsToCanvas(layerId)
			layers.commitRaster(layerId, before)
			return true
		}

		// Ink fill: fill into the clicked cell's ORIGIN-class raster in the current
		// palette's paint group (colour travels + is frozen, same as a stroke).
		// Undo snapshots the groups; stored as alpha geometry, tinted at render.
		const before = layers.snapshotInkGroups(layerId)
		const count = tools.colourCount
		const cls = count > 1 ? classifyColour(patternStore.currentConfig.wpgId, count, placement.transform) ?? 0 : 0
		fillMaskToCanvas(layers.getInkDrawTarget(layerId, paletteIndicesFor(tools.baseColourIndex, count), count, cls), mask, tools.color)
		layers.commitInk(layerId, before)
		return true
	}

	/**
	 * Composite a flood-fill mask onto a canvas as a flat colour (ink fill).
	 * Builds the fill on the temp stroke canvas then source-over blits it, so
	 * existing canvas content outside the mask is preserved (a direct putImageData
	 * would overwrite the whole rect, including transparent pixels).
	 */
	function fillMaskToCanvas(
		canvas: HTMLCanvasElement,
		mask: Uint8Array,
		colorHex: string,
	): void {
		const [r, g, b] = parseHexColor(colorHex)
		const tmp = getStrokeCanvas()
		const tctx = tmp.getContext('2d')!
		tctx.setTransform(1, 0, 0, 1, 0, 0)
		tctx.clearRect(0, 0, TILE_RASTER, TILE_RASTER)
		const img = tctx.createImageData(TILE_RASTER, TILE_RASTER)
		for (let i = 0; i < mask.length; i++) {
			if (mask[i] !== 1) continue
			const o = i * 4
			img.data[o] = r
			img.data[o + 1] = g
			img.data[o + 2] = b
			img.data[o + 3] = 255
		}
		tctx.putImageData(img, 0, 0)
		const ctx = canvas.getContext('2d')!
		ctx.setTransform(1, 0, 0, 1, 0, 0)
		ctx.globalCompositeOperation = 'source-over'
		ctx.globalAlpha = 1
		ctx.drawImage(tmp, 0, 0)
	}

	return {
		drawingActive,
		gestureTick,
		fillAt,
		startStroke,
		continueStroke,
		endStroke,
		cancelStroke,
		startErase,
		continueErase,
		endErase,
		flushDirty,
	}
}
